import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { GoogleMap, PolygonF, MarkerF } from "@react-google-maps/api";
import { Icon } from "@iconify/react";
import { useGoogleMaps } from "../context/GoogleMapsContext";
import api from "../services/api";

/**
 * Custom OverlayView class for rendering calibrated, rotated floorplan graphics on Google Maps
 */
function createGroundOverlayClass() {
  if (!window.google || !window.google.maps || !window.google.maps.OverlayView) {
    return null;
  }

  return class RotatedGroundOverlay extends window.google.maps.OverlayView {
    constructor(bounds, image, rotation, opacity, map) {
      super();
      this.bounds = bounds;
      this.image = image;
      this.rotation = rotation || 0;
      this.opacity = opacity !== undefined && opacity !== null ? opacity : 0.9;
      this.div = null;
      this.setMap(map);
    }

    onAdd() {
      this.div = document.createElement("div");
      this.div.style.borderStyle = "none";
      this.div.style.borderWidth = "0px";
      this.div.style.position = "absolute";
      this.div.style.pointerEvents = "none";
      this.div.style.zIndex = "10";

      const img = document.createElement("img");
      img.src = this.image;
      img.style.width = "100%";
      img.style.height = "100%";
      img.style.position = "absolute";
      img.style.opacity = String(this.opacity);
      img.style.objectFit = "fill";
      this.div.appendChild(img);

      const panes = this.getPanes();
      if (panes && panes.overlayLayer) {
        panes.overlayLayer.appendChild(this.div);
      }
    }

    draw() {
      const overlayProjection = this.getProjection();
      if (!overlayProjection || !this.div) return;

      const sw = overlayProjection.fromLatLngToDivPixel(
        new window.google.maps.LatLng(this.bounds.south, this.bounds.west)
      );
      const ne = overlayProjection.fromLatLngToDivPixel(
        new window.google.maps.LatLng(this.bounds.north, this.bounds.east)
      );

      if (sw && ne) {
        const width = ne.x - sw.x;
        const height = sw.y - ne.y;
        this.div.style.left = `${sw.x}px`;
        this.div.style.top = `${ne.y}px`;
        this.div.style.width = `${Math.max(width, 2)}px`;
        this.div.style.height = `${Math.max(height, 2)}px`;
        this.div.style.transform = `rotate(${this.rotation}deg)`;
        this.div.style.transformOrigin = "center center";

        const img = this.div.querySelector("img");
        if (img) {
          if (img.src !== this.image) img.src = this.image;
          img.style.opacity = String(this.opacity);
        }
      }
    }

    update(bounds, image, rotation, opacity) {
      this.bounds = bounds;
      this.image = image;
      this.rotation = rotation || 0;
      this.opacity = opacity !== undefined && opacity !== null ? opacity : 0.9;
      this.draw();
    }

    onRemove() {
      if (this.div && this.div.parentNode) {
        this.div.parentNode.removeChild(this.div);
        this.div = null;
      }
    }
  };
}

/**
 * Helper to parse geometrics_outline into an array of polygon coordinate arrays:
 * [ [{lat, lng}, ...], ... ]
 */
const parsePolygons = (rawOutline) => {
  if (!rawOutline) return [];
  try {
    const parsed = typeof rawOutline === "string" ? JSON.parse(rawOutline) : rawOutline;
    if (!Array.isArray(parsed) || parsed.length === 0) return [];

    // Format 1: Array of objects with { path: [...] }
    if (parsed[0] && typeof parsed[0] === "object" && Array.isArray(parsed[0].path)) {
      return parsed
        .map((poly) =>
          poly.path
            .map((pt) => ({ lat: Number(pt.lat), lng: Number(pt.lng) }))
            .filter((pt) => !isNaN(pt.lat) && !isNaN(pt.lng))
        )
        .filter((arr) => arr.length >= 3);
    }

    // Format 2: Single polygon array [{lat, lng}, ...]
    if (parsed[0] && typeof parsed[0] === "object" && !Array.isArray(parsed[0]) && "lat" in parsed[0]) {
      const clean = parsed
        .map((pt) => ({ lat: Number(pt.lat), lng: Number(pt.lng) }))
        .filter((pt) => !isNaN(pt.lat) && !isNaN(pt.lng));
      return clean.length >= 3 ? [clean] : [];
    }

    // Format 3: Array of polygon arrays [ [{lat, lng}, ...], ... ]
    if (Array.isArray(parsed[0])) {
      return parsed
        .map((poly) =>
          (Array.isArray(poly) ? poly : [])
            .map((pt) => ({ lat: Number(pt.lat), lng: Number(pt.lng) }))
            .filter((pt) => !isNaN(pt.lat) && !isNaN(pt.lng))
        )
        .filter((arr) => arr.length >= 3);
    }
  } catch (err) {
    console.warn("Failed to parse geometrics_outline:", err);
  }
  return [];
};

const BuildingMapModal = ({ isOpen, onClose, location }) => {
  const { isLoaded } = useGoogleMaps();
  const [map, setMap] = useState(null);
  const [mapTypeId, setMapTypeId] = useState("satellite");
  const [selectedLevel, setSelectedLevel] = useState("OL");
  const [locationDetails, setLocationDetails] = useState(null);
  const [floorplans, setFloorplans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showOutlineWithFloorplan, setShowOutlineWithFloorplan] = useState(false);

  const overlaysRef = useRef([]);

  const activeLocation = locationDetails || location;
  const locId = location?.location_id || location?.id;

  // Fetch latest location details + level floorplans when modal opens
  useEffect(() => {
    if (!isOpen || !locId) {
      setLocationDetails(null);
      setFloorplans([]);
      setSelectedLevel("OL");
      return;
    }

    let isMounted = true;
    setSelectedLevel("OL");
    setLoading(true);

    Promise.allSettled([
      api.get(`/main-locations/${locId}`),
      api.get(`/main-locations/${locId}/floorplans`),
    ])
      .then(([locRes, fpRes]) => {
        if (!isMounted) return;
        if (locRes.status === "fulfilled" && locRes.value?.data?.data) {
          setLocationDetails(locRes.value.data.data);
        } else {
          setLocationDetails(location);
        }

        if (fpRes.status === "fulfilled" && Array.isArray(fpRes.value?.data?.data)) {
          setFloorplans(fpRes.value.data.data);
        } else {
          setFloorplans([]);
        }
      })
      .finally(() => {
        if (isMounted) setLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [isOpen, locId, location]);

  // Parse building outline polygons
  const polygons = useMemo(() => {
    return parsePolygons(activeLocation?.geometrics_outline);
  }, [activeLocation?.geometrics_outline]);

  // Compute ordered level picker items: ["OL", "-1", "0", "1", "2", ...]
  const levelOptions = useMemo(() => {
    const levelSet = new Set();

    const rawAbove = parseInt(activeLocation?.building_levels_above_ground, 10);
    const rawBelow = parseInt(activeLocation?.building_levels_below_ground, 10);

    const aboveCount = !isNaN(rawAbove) && rawAbove > 0 ? rawAbove : 0;
    const minBelow = !isNaN(rawBelow) && rawBelow !== 0 ? (rawBelow < 0 ? rawBelow : -Math.abs(rawBelow)) : 0;

    // Underground levels: e.g. -2, -1
    if (minBelow < 0) {
      for (let i = minBelow; i <= -1; i++) {
        levelSet.add(String(i));
      }
    }

    // Above ground levels (including Ground Floor "0"): e.g. 0, 1, 2
    if (aboveCount > 0) {
      for (let i = 0; i <= aboveCount - 1; i++) {
        levelSet.add(String(i));
      }
    } else if (minBelow < 0) {
      // If underground levels are specified but aboveCount was 0, still include Ground Floor "0"
      levelSet.add("0");
    }

    // Also include any level that has an uploaded floorplan
    floorplans.forEach((fp) => {
      if (fp.level !== undefined && fp.level !== null && String(fp.level).trim() !== "") {
        levelSet.add(String(fp.level).trim());
      }
    });

    // Sort numeric levels ascending (e.g. "-1", "0", "1", "2")
    const sortedLevels = Array.from(levelSet).sort((a, b) => {
      const numA = Number(a);
      const numB = Number(b);
      if (!isNaN(numA) && !isNaN(numB)) return numA - numB;
      return a.localeCompare(b);
    });

    return ["OL", ...sortedLevels];
  }, [
    activeLocation?.building_levels_above_ground,
    activeLocation?.building_levels_below_ground,
    floorplans,
  ]);

  // Map of level -> array of floorplans for that level
  const floorplansByLevel = useMemo(() => {
    const mapByLvl = {};
    floorplans.forEach((fp) => {
      const key = String(fp.level).trim();
      if (!mapByLvl[key]) mapByLvl[key] = [];
      mapByLvl[key].push(fp);
    });
    return mapByLvl;
  }, [floorplans]);

  // Compute fallback bounding box from building outline or location center
  const outlineBounds = useMemo(() => {
    const pts = polygons.flat();
    if (pts.length > 0) {
      return {
        north: Math.max(...pts.map((p) => p.lat)),
        south: Math.min(...pts.map((p) => p.lat)),
        east: Math.max(...pts.map((p) => p.lng)),
        west: Math.min(...pts.map((p) => p.lng)),
      };
    }
    const lat = parseFloat(activeLocation?.lat) || 48.1351;
    const lng = parseFloat(activeLocation?.lng) || 11.582;
    return {
      north: lat + 0.0004,
      south: lat - 0.0004,
      east: lng + 0.0005,
      west: lng - 0.0005,
    };
  }, [polygons, activeLocation?.lat, activeLocation?.lng]);

  // Center & zoom map tightly on the building outline or location coordinates
  const fitMapToBuilding = useCallback(
    (mapInstance) => {
      const targetMap = mapInstance || map;
      if (!targetMap || !window.google) return;

      const pts = polygons.flat();
      if (pts.length > 0) {
        const bounds = new window.google.maps.LatLngBounds();
        pts.forEach((pt) => bounds.extend(pt));
        targetMap.fitBounds(bounds, 48);
        return;
      }

      // If no outline polygons, check if any floorplans have calibrated bounds
      const validFp = floorplans.find(
        (fp) => fp.north && fp.south && fp.east && fp.west
      );
      if (validFp) {
        const bounds = new window.google.maps.LatLngBounds(
          new window.google.maps.LatLng(Number(validFp.south), Number(validFp.west)),
          new window.google.maps.LatLng(Number(validFp.north), Number(validFp.east))
        );
        targetMap.fitBounds(bounds, 48);
        return;
      }

      const lat = parseFloat(activeLocation?.lat);
      const lng = parseFloat(activeLocation?.lng);
      if (!isNaN(lat) && !isNaN(lng)) {
        targetMap.setCenter({ lat, lng });
        targetMap.setZoom(19);
      }
    },
    [map, polygons, floorplans, activeLocation?.lat, activeLocation?.lng]
  );

  // Re-fit when polygons/locationDetails finish loading
  useEffect(() => {
    if (map && isOpen) {
      fitMapToBuilding(map);
    }
  }, [map, isOpen, polygons, fitMapToBuilding]);

  // Clean up custom Ground Overlays helper
  const clearGroundOverlays = useCallback(() => {
    overlaysRef.current.forEach((ov) => {
      if (ov) ov.setMap(null);
    });
    overlaysRef.current = [];
  }, []);

  useEffect(() => {
    return () => {
      clearGroundOverlays();
    };
  }, [clearGroundOverlays]);

  // Manage Ground Overlays whenever selectedLevel or floorplans change
  useEffect(() => {
    clearGroundOverlays();

    if (!map || !window.google || !isOpen || selectedLevel === "OL") {
      return;
    }

    const levelPlans = floorplansByLevel[String(selectedLevel)] || [];
    if (levelPlans.length === 0) return;

    const OverlayClass = createGroundOverlayClass();
    if (!OverlayClass) return;

    levelPlans.forEach((fp) => {
      if (!fp.image_url) return;

      const hasCalibratedBounds =
        fp.north !== null &&
        fp.north !== undefined &&
        fp.south !== null &&
        fp.south !== undefined &&
        fp.east !== null &&
        fp.east !== undefined &&
        fp.west !== null &&
        fp.west !== undefined &&
        (Number(fp.north) !== 0 || Number(fp.south) !== 0);

      const bounds = hasCalibratedBounds
        ? {
            north: parseFloat(fp.north),
            south: parseFloat(fp.south),
            east: parseFloat(fp.east),
            west: parseFloat(fp.west),
          }
        : outlineBounds;

      const rotation = parseFloat(fp.rotation) || 0;
      const opacity =
        fp.opacity !== undefined && fp.opacity !== null
          ? parseFloat(fp.opacity)
          : 0.92;

      const instance = new OverlayClass(
        bounds,
        fp.image_url,
        rotation,
        opacity,
        map
      );
      overlaysRef.current.push(instance);
    });
  }, [
    map,
    isOpen,
    selectedLevel,
    floorplansByLevel,
    outlineBounds,
    clearGroundOverlays,
  ]);

  if (!isOpen || !location) return null;

  const activeLevelPlans =
    selectedLevel !== "OL" ? floorplansByLevel[String(selectedLevel)] || [] : [];
  const shouldRenderPolygon =
    selectedLevel === "OL" ||
    showOutlineWithFloorplan ||
    (selectedLevel !== "OL" && activeLevelPlans.length === 0);

  const defaultCenter = {
    lat: parseFloat(activeLocation?.lat) || 48.1351,
    lng: parseFloat(activeLocation?.lng) || 11.582,
  };

  return (
    <div
      className="modal fade show d-block"
      style={{ backgroundColor: "rgba(15, 23, 42, 0.65)", zIndex: 1060 }}
      onClick={onClose}
    >
      <div
        className="modal-dialog modal-xl modal-dialog-centered"
        style={{ maxWidth: "1100px" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="modal-content border-0 overflow-hidden"
          style={{
            borderRadius: "14px",
            boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.35)",
          }}
        >
          {/* ===== MODAL HEADER ===== */}
          <div className="modal-header bg-white border-bottom px-24 py-16 d-flex align-items-center justify-content-between">
            <div className="d-flex align-items-center gap-12">
              <div
                className="w-40-px h-40-px rounded-circle d-flex align-items-center justify-content-center"
                style={{ backgroundColor: "#111827", color: "#ffffff" }}
              >
                <Icon icon="solar:buildings-2-bold" className="text-xl" />
              </div>
              <div>
                <div className="d-flex align-items-center gap-8">
                  <h6 className="mb-0 fw-bold text-primary-light" style={{ fontSize: "16px" }}>
                    {activeLocation?.location_name || "Location Building Map"}
                  </h6>
                  {activeLocation?.store_code && (
                    <span className="badge bg-neutral-100 text-neutral-700 border text-2xs px-8 py-4 radius-4 font-monospace">
                      {activeLocation.store_code}
                    </span>
                  )}
                </div>
                <div className="text-xs text-secondary-light mt-2 d-flex align-items-center gap-6">
                  <Icon icon="solar:map-point-linear" className="text-sm" />
                  <span>
                    {[
                      [activeLocation?.street, activeLocation?.street_number]
                        .filter(Boolean)
                        .join(" "),
                      [activeLocation?.postal_code, activeLocation?.city]
                        .filter(Boolean)
                        .join(" "),
                    ]
                      .filter(Boolean)
                      .join(", ") || `${defaultCenter.lat.toFixed(5)}, ${defaultCenter.lng.toFixed(5)}`}
                  </span>
                </div>
              </div>
            </div>

            <div className="d-flex align-items-center gap-12">
              {/* Current Mode Badge */}
              <span
                className="badge px-12 py-6 radius-6 text-xs fw-semibold d-inline-flex align-items-center gap-6"
                style={{
                  backgroundColor: "#f3f4f6",
                  color: "#111827",
                  border: "1px solid #e5e7eb",
                }}
              >
                <Icon
                  icon={
                    selectedLevel === "OL"
                      ? "solar:Scanner-linear"
                      : "solar:layers-minimalistic-bold"
                  }
                  className="text-sm"
                />
                {selectedLevel === "OL"
                  ? `Building Outline (${polygons.length} Polygon${polygons.length === 1 ? "" : "s"})`
                  : `Level ${selectedLevel}${
                      activeLevelPlans[0]?.name ? ` — ${activeLevelPlans[0].name}` : ""
                    }`}
              </span>

              <button
                type="button"
                className="btn-close shadow-none"
                onClick={onClose}
                aria-label="Close"
              />
            </div>
          </div>

          {/* ===== MODAL BODY (MAP + OVERLAYS) ===== */}
          <div className="modal-body p-0 position-relative" style={{ height: "620px", backgroundColor: "#0f172a" }}>
            {!isLoaded ? (
              <div className="w-100 h-100 d-flex flex-column align-items-center justify-content-center text-white">
                <div className="spinner-border text-light mb-12" role="status" />
                <span className="text-sm">Loading Google Maps...</span>
              </div>
            ) : (
              <GoogleMap
                mapContainerStyle={{ width: "100%", height: "100%" }}
                defaultCenter={defaultCenter}
                defaultZoom={19}
                onLoad={(mapInstance) => {
                  setMap(mapInstance);
                  mapInstance.setTilt(0);
                  fitMapToBuilding(mapInstance);
                }}
                options={{
                  mapTypeId,
                  tilt: 0,
                  mapTypeControl: false,
                  streetViewControl: false,
                  fullscreenControl: false,
                  zoomControl: true,
                  zoomControlOptions: {
                    position: window.google?.maps?.ControlPosition?.LEFT_BOTTOM,
                  },
                  gestureHandling: "greedy",
                }}
              >
                {/* Building Outline Polygons */}
                {shouldRenderPolygon &&
                  polygons.map((path, idx) => (
                    <PolygonF
                      key={`outline-poly-${idx}`}
                      paths={path}
                      options={{
                        fillColor: selectedLevel === "OL" ? "#3b82f6" : "#111827",
                        fillOpacity: selectedLevel === "OL" ? 0.28 : 0.04,
                        strokeColor: selectedLevel === "OL" ? "#60a5fa" : "#facc15",
                        strokeOpacity: 0.95,
                        strokeWeight: selectedLevel === "OL" ? 3 : 2,
                        clickable: false,
                        zIndex: 5,
                      }}
                    />
                  ))}

                {/* Fallback Center Pin if no outline and no floorplans */}
                {polygons.length === 0 && floorplans.length === 0 && (
                  <MarkerF position={defaultCenter} />
                )}
              </GoogleMap>
            )}

            {/* ===== TOP-LEFT FLOATING CONTROLS: MAP TYPE & RE-CENTER ===== */}
            <div
              className="position-absolute d-flex align-items-center gap-8"
              style={{ top: "16px", left: "16px", zIndex: 20 }}
            >
              <div
                className="d-inline-flex p-4 radius-8"
                style={{
                  backgroundColor: "rgba(17, 24, 39, 0.88)",
                  backdropFilter: "blur(8px)",
                  border: "1px solid rgba(255,255,255,0.14)",
                }}
              >
                {[
                  { id: "satellite", label: "Satellite" },
                  { id: "hybrid", label: "Hybrid" },
                  { id: "roadmap", label: "Map" },
                ].map((t) => {
                  const active = mapTypeId === t.id;
                  return (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => setMapTypeId(t.id)}
                      className="border-0 px-12 py-6 radius-6 text-xs fw-semibold transition-all"
                      style={{
                        backgroundColor: active ? "#ffffff" : "transparent",
                        color: active ? "#111827" : "rgba(255,255,255,0.8)",
                      }}
                    >
                      {t.label}
                    </button>
                  );
                })}
              </div>

              <button
                type="button"
                onClick={() => fitMapToBuilding(map)}
                className="border-0 px-12 py-8 radius-8 text-xs fw-semibold d-inline-flex align-items-center gap-6"
                style={{
                  backgroundColor: "rgba(17, 24, 39, 0.88)",
                  backdropFilter: "blur(8px)",
                  color: "#ffffff",
                  border: "1px solid rgba(255,255,255,0.14)",
                }}
                title="Zoom to Building"
              >
                <Icon icon="solar:focus-bold" className="text-sm" />
                <span>Fit Building</span>
              </button>

              {selectedLevel !== "OL" && polygons.length > 0 && (
                <button
                  type="button"
                  onClick={() => setShowOutlineWithFloorplan((prev) => !prev)}
                  className="border-0 px-12 py-8 radius-8 text-xs fw-semibold d-inline-flex align-items-center gap-6"
                  style={{
                    backgroundColor: showOutlineWithFloorplan
                      ? "#facc15"
                      : "rgba(17, 24, 39, 0.88)",
                    backdropFilter: "blur(8px)",
                    color: showOutlineWithFloorplan ? "#111827" : "#ffffff",
                    border: "1px solid rgba(255,255,255,0.14)",
                  }}
                  title="Toggle Building Outline Overlay"
                >
                  <Icon icon="solar:maximize-square-minimalistic-linear" className="text-sm" />
                  <span>Outline {showOutlineWithFloorplan ? "On" : "Off"}</span>
                </button>
              )}
            </div>

            {/* ===== RIGHT / BOTTOM FLOATING LEVEL PICKER ("OL", "-1", "0", "1", "2") ===== */}
            <div
              className="position-absolute d-flex flex-column align-items-center"
              style={{
                right: "20px",
                top: "50%",
                transform: "translateY(-50%)",
                zIndex: 25,
              }}
            >
              <div
                className="d-flex flex-column align-items-center p-6 radius-12 gap-6"
                style={{
                  backgroundColor: "rgba(17, 24, 39, 0.92)",
                  backdropFilter: "blur(10px)",
                  border: "1px solid rgba(255,255,255,0.16)",
                  boxShadow: "0 12px 30px rgba(0,0,0,0.4)",
                  maxHeight: "480px",
                  overflowY: "auto",
                }}
              >
                <div
                  className="text-2xs fw-bold text-uppercase px-6 pt-2 pb-4 border-bottom w-100 text-center"
                  style={{
                    color: "rgba(255,255,255,0.55)",
                    letterSpacing: "0.08em",
                    borderColor: "rgba(255,255,255,0.12)",
                  }}
                >
                  Level
                </div>

                {levelOptions.map((lvl) => {
                  const isActive = String(selectedLevel) === String(lvl);
                  const isOL = lvl === "OL";
                  const hasGraphic = isOL
                    ? polygons.length > 0
                    : Boolean(floorplansByLevel[String(lvl)]?.length);

                  return (
                    <button
                      key={lvl}
                      type="button"
                      onClick={() => setSelectedLevel(lvl)}
                      className="border-0 d-flex align-items-center justify-content-center fw-bold position-relative transition-all"
                      style={{
                        width: "46px",
                        height: "40px",
                        borderRadius: "8px",
                        fontSize: "13px",
                        backgroundColor: isActive
                          ? "#ffffff"
                          : "rgba(255, 255, 255, 0.07)",
                        color: isActive
                          ? "#111827"
                          : hasGraphic
                          ? "#ffffff"
                          : "rgba(255, 255, 255, 0.45)",
                        boxShadow: isActive
                          ? "0 4px 12px rgba(0, 0, 0, 0.25)"
                          : "none",
                      }}
                      title={
                        isOL
                          ? "OL — Building Outline"
                          : hasGraphic
                          ? `Level ${lvl} — Calibrated Floorplan Graphic`
                          : `Level ${lvl} — No floorplan graphic uploaded yet`
                      }
                    >
                      <span>{lvl}</span>
                      {/* Status indicator dot for calibrated floorplan / outline */}
                      {hasGraphic && (
                        <span
                          style={{
                            position: "absolute",
                            top: "5px",
                            right: "5px",
                            width: "6px",
                            height: "6px",
                            borderRadius: "50%",
                            backgroundColor: isOL
                              ? "#3b82f6"
                              : isActive
                              ? "#10b981"
                              : "#34d399",
                          }}
                        />
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* ===== BOTTOM STATUS PILL WHEN A LEVEL HAS NO FLOORPLAN GRAPHIC ===== */}
            {!loading && selectedLevel !== "OL" && activeLevelPlans.length === 0 && (
              <div
                className="position-absolute px-16 py-10 radius-8 d-flex align-items-center gap-8 text-xs fw-medium"
                style={{
                  bottom: "20px",
                  left: "50%",
                  transform: "translateX(-50%)",
                  backgroundColor: "rgba(17, 24, 39, 0.9)",
                  color: "#f9fafb",
                  border: "1px solid rgba(255,255,255,0.15)",
                  zIndex: 20,
                }}
              >
                <Icon icon="solar:info-circle-bold" className="text-warning-main text-base" />
                <span>
                  No calibrated floorplan graphic uploaded for <strong>Level {selectedLevel}</strong> yet. Showing building outline for reference.
                </span>
              </div>
            )}

            {/* ===== BOTTOM STATUS PILL WHEN "OL" HAS NO OUTLINE YET ===== */}
            {!loading && selectedLevel === "OL" && polygons.length === 0 && (
              <div
                className="position-absolute px-16 py-10 radius-8 d-flex align-items-center gap-8 text-xs fw-medium"
                style={{
                  bottom: "20px",
                  left: "50%",
                  transform: "translateX(-50%)",
                  backgroundColor: "rgba(17, 24, 39, 0.9)",
                  color: "#f9fafb",
                  border: "1px solid rgba(255,255,255,0.15)",
                  zIndex: 20,
                }}
              >
                <Icon icon="solar:info-circle-bold" className="text-warning-main text-base" />
                <span>No building outline polygon saved for this location yet.</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BuildingMapModal;

